/**
 * Document-visibility helpers for the sync coordinator.
 *
 * Why sync cares: a hidden tab is not merely idle. Chrome throttles its
 * timers, and after a few minutes of being backgrounded it may *freeze*
 * the page entirely. A request issued in that state doesn't fail fast —
 * it sits on a socket the OS has already torn down, and the XHR timeout
 * callback isn't dispatched until the page thaws. That is why QPL saw
 * `http_call` flows whose ended_at was 10 seconds after started_at when
 * the tab was merely throttled, and up to half an hour when it was
 * frozen outright. Waiting for visibility before issuing the cycle turns
 * a guaranteed loss into a short delay.
 */

import { buffers, eventChannel, EventChannel } from 'redux-saga';

export function isTabHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/**
 * Emits once the document is visible. If it already is, the emit happens
 * synchronously inside the factory — hence the explicit buffer: with
 * redux-saga's default (`buffers.none()`) a synchronous emit lands before
 * anyone is taking and is dropped on the floor, which would park the
 * coordinator forever on a tab that was never hidden to begin with.
 */
export function createVisibleChannel(): EventChannel<true> {
  return eventChannel<true>((emit) => {
    const handler = () => {
      if (!isTabHidden()) emit(true);
    };
    if (typeof document === 'undefined') {
      emit(true);
      return () => {};
    }
    document.addEventListener('visibilitychange', handler);
    // Closes the check-then-subscribe race: if the tab became visible
    // between the caller's isTabHidden() test and this subscription, the
    // visibilitychange event is already gone.
    handler();
    return () => document.removeEventListener('visibilitychange', handler);
  }, buffers.expanding(1));
}
